import { Amplify } from 'aws-amplify';
import {
  confirmSignIn,
  fetchAuthSession,
  FetchAuthSessionOptions,
  fetchUserAttributes,
  forgetDevice,
  getCurrentUser,
  rememberDevice,
  signIn,
  SignInOutput,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolClientId: process.env.REACT_APP_COGNITO_USER_CLIENT_ID || '',
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
    },
  },
});

export type UserSession = {
  username: string;
  tokens: {
    IdToken: string;
    AccessToken: string;
    RefreshToken: string;
  };
};

export type AuthenticateProps = {
  username?: string;
  password?: string;
  remember?: boolean;
  oldPassword?: string;
};

export type UserAttributes = {
  [key: string]: string;
};

// Amplify resolves with empty tokens instead of throwing when there is no
// session or the refresh token has been revoked.
const getSession = async (
  options?: FetchAuthSessionOptions
): Promise<UserSession> => {
  const { tokens } = await fetchAuthSession(options);
  if (!tokens?.idToken || !tokens.accessToken) {
    throw new Error('no session');
  }
  const { username } = await getCurrentUser();

  return {
    tokens: {
      AccessToken: tokens.accessToken.toString(),
      IdToken: tokens.idToken.toString(),
      RefreshToken: '',
    },
    username,
  };
};

// forgetDevice throws when there is no device key, and device tracking must
// never block sign-in.
const applyRememberDevice = async (remember?: boolean) => {
  try {
    if (remember) {
      await rememberDevice();
    } else {
      await forgetDevice();
    }
  } catch {
    // ignore
  }
};

export type LoginResult =
  | { otpRequired: false; session: UserSession }
  | { otpRequired: true; destination?: string };

const resolveSignInStep = async (
  output: SignInOutput,
  email: string,
  remember?: boolean
): Promise<LoginResult> => {
  const { isSignedIn, nextStep } = output;
  if (isSignedIn) {
    await applyRememberDevice(remember);
    return { otpRequired: false, session: await getSession() };
  }

  switch (nextStep.signInStep) {
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      return {
        destination: nextStep.codeDeliveryDetails?.destination,
        otpRequired: true,
      };
    case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
    case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
      return resolveSignInStep(
        await confirmSignIn({ challengeResponse: 'EMAIL' }),
        email,
        remember
      );
    case 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP':
      return resolveSignInStep(
        await confirmSignIn({ challengeResponse: email }),
        email,
        remember
      );
    default:
      throw new Error(`Unsupported sign-in step: ${nextStep.signInStep}`);
  }
};

export const login = async (props: AuthenticateProps): Promise<LoginResult> => {
  const username = (props.username || '').toLowerCase();

  // signIn throws UserAlreadyAuthenticatedException if tokens are still stored.
  await signOut().catch(() => undefined);

  const output = await signIn({
    password: props.password,
    username,
  });
  return resolveSignInStep(output, username, props.remember);
};

export const confirmEmailOtp = async (
  code: string,
  remember?: boolean
): Promise<UserSession> => {
  const { isSignedIn, nextStep } = await confirmSignIn({
    challengeResponse: code.trim(),
  });
  if (!isSignedIn) {
    throw new Error(`Unsupported sign-in step: ${nextStep.signInStep}`);
  }
  await applyRememberDevice(remember);
  return getSession();
};

export const logout = () => {
  signOut().catch(() => undefined);
};

export const getUserSessionAttributes = async (): Promise<
  [UserSession, UserAttributes]
> => {
  const session = await getSession();
  const result = await fetchUserAttributes();

  const attributes: UserAttributes = {};
  for (const [key, value] of Object.entries(result)) {
    if (value !== undefined) {
      attributes[key] = value;
    }
  }
  return [session, attributes];
};

export const refreshSession = (): Promise<UserSession> => {
  return getSession({ forceRefresh: true });
};

export const changePassword = async (props: AuthenticateProps) => {
  await getSession();
  await updatePassword({
    newPassword: props.password || '',
    oldPassword: props.oldPassword || '',
  });
  return 'SUCCESS' as const;
};
